import { zodResolver } from '@hookform/resolvers/zod';
import { SearchIcon } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { FormGroup } from '@/components/FormGroup';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

const searchSchema = z.object({
  searchTerm: z.string(),
});

type SearchFormData = z.infer<typeof searchSchema>;

interface SearchFormProps {
  searchQuery: string;
  isLoading: boolean;
  onSearchSubmit: (searchTerm: string) => void;
  onClear: () => void;
}

export function SearchForm({
  searchQuery,
  isLoading,
  onSearchSubmit,
  onClear,
}: SearchFormProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'pages.Personalization.components.Search',
  });

  const {
    formState: { isValid, errors, isSubmitting },
    register,
    handleSubmit,
    reset,
  } = useForm<SearchFormData>({
    resolver: zodResolver(searchSchema),
    defaultValues: {
      searchTerm: searchQuery,
    },
  });

  useEffect(() => {
    reset({ searchTerm: searchQuery });
  }, [searchQuery, reset]);

  const isValidForm = isValid && !isSubmitting;

  const onSubmit = handleSubmit((data: SearchFormData) => {
    onSearchSubmit(data.searchTerm);
  });

  function handleClear() {
    reset({ searchTerm: '' });
    onClear();
  }

  return (
    <div className="mb-6 rounded-lg border bg-white p-6 shadow-sm transition-all duration-200 hover:shadow-md">
      <h3 className="mb-1 text-xl font-medium">{t('title')}</h3>

      <p className="mb-3 text-sm text-gray-600">{t('description')}</p>

      <form onSubmit={onSubmit} className="flex flex-col gap-3 sm:flex-row">
        <div className="flex-1">
          <FormGroup
            error={{ message: errors.searchTerm?.message, font: 'sm' }}
          >
            <div className="relative">
              <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-muted-foreground" />
              <Input
                id="searchTerm"
                placeholder={t('placeholder')}
                className="pl-10"
                {...register('searchTerm')}
              />
              {isLoading && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2 transform">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                </div>
              )}
            </div>
          </FormGroup>
        </div>

        <Button
          type="submit"
          className="flex items-center gap-2"
          disabled={!isValidForm || isLoading}
        >
          <SearchIcon
            className={`h-4 w-4 ${isSubmitting ? 'animate-spin' : ''}`}
          />
          {isSubmitting ? t('searchingButton') : t('searchButton')}
        </Button>

        {searchQuery && (
          <Button
            type="button"
            variant="outline"
            className="flex items-center gap-2"
            onClick={handleClear}
          >
            {t('clearButton')}
          </Button>
        )}
      </form>
    </div>
  );
}
